import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { MFADeviceList } from "./MFADeviceList";
import { MFAEnrollment } from "./MFAEnrollment";

type MFAFactor = {
  id: string;
  friendly_name?: string;
  status?: string;
  factor_type?: string;
};

export const SecuritySettings = () => {
  const [mfaFactors, setMfaFactors] = useState<MFAFactor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEnrollment, setShowEnrollment] = useState(false);

  const loadMFAFactors = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;

      setMfaFactors(data?.totp || []);
    } catch (error) {
      console.error("Error loading MFA factors:", error);
      toast.error("Failed to load MFA devices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMFAFactors();
  }, []);

  const handleEnrollmentComplete = () => {
    setShowEnrollment(false);
    loadMFAFactors();
    toast.success("MFA device enrolled successfully");
  };

  const handleUnenroll = async (factorId: string) => {
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;

      loadMFAFactors();
      toast.success("MFA device removed successfully");
    } catch (error) {
      console.error("Error unenrolling MFA factor:", error);
      toast.error("Failed to remove MFA device");
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Security Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Loading...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Multi-Factor Authentication</CardTitle>
          <CardDescription>Add an extra layer of security to your account with MFA</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {mfaFactors.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">No MFA devices enrolled</p>
              <p className="text-sm text-muted-foreground mb-6">
                Set up multi-factor authentication to secure your account
              </p>
              <Button onClick={() => setShowEnrollment(true)}>Set up MFA</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {mfaFactors.length} device{mfaFactors.length !== 1 ? "s" : ""} enrolled
                </p>
                <Button variant="outline" onClick={() => setShowEnrollment(true)}>
                  Add Device
                </Button>
              </div>
              <MFADeviceList factors={mfaFactors} onUnenroll={handleUnenroll} />
            </div>
          )}
        </CardContent>
      </Card>

      {showEnrollment && (
        <MFAEnrollment onComplete={handleEnrollmentComplete} onCancel={() => setShowEnrollment(false)} />
      )}
    </div>
  );
};
